import { useState, useEffect, useRef, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useEmailFirstLogin } from './hooks/useEmailFirstLogin'
import RegisterPage from './pages/RegisterPage'
import { ToastProvider } from './components/ui'
import { ErrorBoundary } from './components/ErrorBoundary'
import Layout from './components/Layout'
import { useStaff } from './hooks/useStaff'
import Dashboard from './pages/Dashboard'
import Members from './pages/Members'
import Services from './pages/Services'
import Marketplace from './pages/Marketplace'
import GDataPartner from './pages/GDataPartner'
import ScoutLandingPage from './pages/ScoutLandingPage'
import Settings from './pages/Settings'
import CortexPage from './pages/CortexPage'
import InfraPage from './pages/InfraPage'
import ComingSoon from './components/ComingSoon'
import { isComingSoon } from './lib/comingSoon'
import Billing from './pages/Billing'
import Licenses from './pages/Licenses'
import Documents from './pages/Documents'
import Projects from './pages/Projects'
import ProjectDetail from './pages/ProjectDetail'
import Tickets from './pages/Tickets'
import Tasks from './pages/Tasks'
import OnboardingWizard from './pages/OnboardingWizard'
import SelectOrganization from './pages/SelectOrganization'
import Downloads from './pages/Downloads'
import StaffKnowledge from './pages/StaffKnowledge'
import StaffGeschaeftOpsHub from './pages/staff/StaffGeschaeftOpsHub'
import StaffCortexAdmin from './pages/StaffCortexAdmin'
import StaffHealthHub from './pages/staff/StaffHealthHub'
import StaffGeschaeft from './pages/StaffGeschaeft'
import StaffCustomersHub from './pages/staff/StaffCustomersHub'
import StaffLicenses from './pages/StaffLicenses'
import StaffEarnings from './pages/StaffEarnings'
import Usage from './pages/Usage'
import StaffUsage from './pages/staff/StaffUsage'
import { api } from './services/api'
import { isPersonalMode, isCustomDomain, isTenantInstance, getRuntimeConfig } from './lib/runtimeConfig'
import { routerBasename } from './lib/tenantPath'

interface Organization {
  id: number
  public_token?: string  // UUID — fuer URL-Routing (Datenschutz). Optional fuer Backwards-Compat mit alten Backend-Versionen.
  name: string
  slug: string           // intern; nur fuer Backwards-Compat / interne Refs
  member_role: string
  member_count: number
  realm_status?: string
  needs_onboarding?: boolean  // TQ #5325: true wenn cortex-db.org_settings.soul_addendum leer/null
  caller_onboarding_status?: 'pending' | 'completed' | 'skipped'  // TQ #5707-a/#5710: per-Member Mode (Default completed wenn Row fehlt)
}

// UUID v4-Form: 8-4-4-4-12 hex digits.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Inner component: reads slug-or-token from URL (Staff-Cockpit) ODER
// resolved via Hostname (Custom-Domain wie cockpit.eza-wolpertshausen.de).
// Datenschutz: keine Slug-URL mehr auf Custom-Domain — siehe TQ #5387.
function CockpitApp({ onLogout, keycloakApi, customDomain = false, personalMode = false }: { onLogout: () => void; keycloakApi: any; customDomain?: boolean; personalMode?: boolean }) {
  // TQ #6906 Bundle A2: Personal-Modus (with.it-sce.de) → kein Backend-Call,
  // Pseudo-Org wird Frontend-only gerendert. Backend filtert per-User Resources
  // ueber owner_user_id (Phase B2). customDomain=true setzt den base-Path
  // analog cockpit.<tenant-domain> (kein /:slug Prefix).
  const personalPseudoOrg = personalMode ? buildPersonalPseudoOrg() : null

  // Im Slug-Modus: Param 'slug' kommt aus URL (kann auch UUID sein).
  // Im Custom-Domain-Modus: kein URL-Param, Org wird via /api/public/org/by-domain/<hostname> resolved.
  const { slug: slugOrToken } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [org, setOrg] = useState<Organization | null>(personalPseudoOrg)
  const [loading, setLoading] = useState(!personalMode)
  const [notFound, setNotFound] = useState(false)
  // Kunden-Instanz: 401 = neu anmelden, 403 = keine Client-Rolle (deny by default).
  const [authFailure, setAuthFailure] = useState<401 | 403 | null>(null)
  // TQ #6906 Bundle A1: isStaff fuer Sidebar Staff-Sektion (cockpit.it-sce.de)
  const { isStaff } = useStaff()
  // TQ #6906 Patric-Klarstellung 2026-06-02: gebuchte Module aus Cortex
  // (tenant_rate_configs.allowed_modules). Steuert Sidebar-Sichtbarkeit von
  // aria/infra im Personal-Mode (Aria-Tab nur wenn 'aria' gebucht).
  // Org/Staff-Mode bleibt heutiges Verhalten (Tab default an, Backend gated).
  const [subscribedModules, setSubscribedModules] = useState<string[]>([])

  useEffect(() => {
    const loadModules = async () => {
      try {
        // TQ #10075: /api/v1/org/token-budget ist ein Cortex-interner Pfad
        // (nie ein portal-api-Route), 404 garantiert ueber keycloakApi (portal-api
        // baseURL). /api/me/capabilities (TQ #7668) ist der Portal-facing
        // Endpoint, der dieselbe Cortex-Quelle (tenant_rate_configs.allowed_modules)
        // per-User spiegelt — funktioniert org+personal+staff (nur KeycloakAuth).
        // Kunden-Instanz: /api/me/* ist zentral und in cockpit-api nicht vorhanden.
        if (isTenantInstance()) return
        const resp = await keycloakApi.get('/api/me/capabilities')
        const caps = resp.data?.capabilities as { module: string; available: boolean }[] | undefined
        if (Array.isArray(caps)) {
          const mods = caps.filter((c) => c.available).map((c) => c.module)
          setSubscribedModules(mods.map((m: string) => m.toLowerCase()))
        }
      } catch {
        // Endpoint nicht erreichbar / 401 → leere Liste, Layout-Default greift
      }
    }
    loadModules()
  }, [keycloakApi])

  useEffect(() => {
    // TQ #6906 Bundle A2: Personal-Modus benoetigt keinen Backend-Call —
    // org wurde direkt via buildPersonalPseudoOrg() im useState gesetzt.
    if (personalMode) {
      setLoading(false)
      return
    }
    setLoading(true)
    setNotFound(false)

    const load = async () => {
      try {
        if (isTenantInstance()) {
          const who = await keycloakApi.get(`/api/org/${getRuntimeConfig().orgRef}/whoami`)
          setOrg(buildTenantPseudoOrg(String(who.data?.role ?? '')))
        } else if (customDomain) {
          // Custom-Domain-Pfad: zuerst public by-domain Resolver → token bekommen,
          // dann authentifizierter GET /api/org/<token> für die vollen Org-Daten.
          const hostname = window.location.hostname
          const pub = await keycloakApi.get(`/api/public/org/by-domain/${encodeURIComponent(hostname)}`)
          const token = pub.data.public_token || pub.data.slug
          const detail = await keycloakApi.get(`/api/org/${token}`)
          if (detail.data.organization) {
            setOrg(detail.data.organization)
          } else {
            setNotFound(true)
          }
        } else {
          if (!slugOrToken) {
            setNotFound(true)
            return
          }
          const response = await keycloakApi.get(`/api/org/${slugOrToken}`)
          if (response.data.organization) {
            setOrg(response.data.organization)
          } else {
            setNotFound(true)
          }
        }
      } catch (e: any) {
        const status = e?.response?.status
        if (isTenantInstance() && (status === 401 || status === 403)) setAuthFailure(status)
        else setNotFound(true)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [slugOrToken, keycloakApi, customDomain, personalMode])

  // Datenschutz-Redirect: wenn die URL den lesbaren Slug enthaelt (nicht UUID-Form)
  // und wir den public_token kennen, ersetze /<slug>/... transparent durch
  // /<public_token>/... (replace=true, kein History-Eintrag).
  // Nur im Slug-Modus relevant — Custom-Domain hat keinen URL-Suffix.
  useEffect(() => {
    if (customDomain) return
    if (!org || !org.public_token || !slugOrToken) return
    const isUUID = UUID_RE.test(slugOrToken)
    if (!isUUID && slugOrToken !== org.public_token) {
      const newPath = location.pathname.replace(`/${slugOrToken}`, `/${org.public_token}`)
      navigate(newPath + location.search + location.hash, { replace: true })
    }
  }, [org, slugOrToken, location, navigate, customDomain])

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">IT-SCE</div>
          <div className="spinner"></div>
          <p>Organisation wird geladen...</p>
        </div>
      </div>
    )
  }

  if (authFailure) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">{getRuntimeConfig().customerLabel}</div>
          {authFailure === 401 ? (
            <>
              <p>Die Sitzung ist abgelaufen.</p>
              <button onClick={() => window.location.reload()} style={{ marginTop: '16px' }}>Neu anmelden</button>
            </>
          ) : (
            <p>Keine Berechtigung für dieses Cockpit. Bitte IT-SCE kontaktieren.</p>
          )}
        </div>
      </div>
    )
  }

  if (notFound || !org) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">IT-SCE</div>
          <p>Organisation nicht gefunden.</p>
          <a href="https://with.it-sce.de" style={{ marginTop: '16px', color: 'var(--brand-primary, #FF6B35)' }}>
            Zurück zum Portal
          </a>
        </div>
      </div>
    )
  }

  // base nutzt public_token wenn vorhanden, sonst Fallback auf slug — aber NUR im
  // Slug-Modus. Auf Custom-Domain (cockpit.<tenant-domain>) ist die URL
  // bereits ohne Suffix; base = '' liefert relative Pfade zur Root (TQ #5387).
  const base = customDomain ? '' : `/${org.public_token || org.slug}`

  // TQ #5325: First-Login-Detection. Tenant-Owner/Admin sieht beim ersten
  // Aufruf den Scout-Onboarding-Wizard, bevor das Cockpit erscheint —
  // damit Aria das Persona-Profil bekommt. Member ohne Admin-Rechte
  // sehen den Dashboard wie bisher (sie koennen das Profil nicht persistieren).
  const isAdminLike = org.member_role === 'owner' || org.member_role === 'admin'
  const onPath = location.pathname
  const onWizard = onPath.endsWith('/onboarding-wizard')
  if (org.needs_onboarding && isAdminLike && !onWizard) {
    return (
      <OnboardingWizard
        organization={org}
        onComplete={() => {
          setOrg(prev => prev ? { ...prev, needs_onboarding: false } : prev)
        }}
      />
    )
  }

  return (
    <Layout
      organization={org}
      onLogout={onLogout}
      customDomain={customDomain}
      isStaff={isStaff}
      subscribedModules={subscribedModules}
      onOnboardingCompleted={() => {
        setOrg(prev => prev ? { ...prev, caller_onboarding_status: 'completed' } : prev)
      }}
    >
      {/* TQ #6007: ErrorBoundary umhuellt alle Routes. Eine kaputte Seite
          (z.B. Downloads bei platforms=null) zeigt jetzt ein Fallback-UI
          statt die komplette App + Sidebar zu killen. */}
      <ErrorBoundary>
        <Routes>
        <Route
          path="onboarding-wizard"
          element={
            <OnboardingWizard
              organization={org}
              onComplete={() => {
                setOrg(prev => prev ? { ...prev, needs_onboarding: false } : prev)
              }}
            />
          }
        />
        {/* TQ #6453: /onboarding ist Alias auf /onboarding-wizard. Custom-Domain
            (cockpit.<tenant>.<domain>/onboarding) und Staff-Cockpit-Subpath
            (/<slug>/onboarding) sollen beide den Wizard zeigen, damit
            Re-Onboarding zugaenglich ist. */}
        <Route path="onboarding" element={<Navigate to={base ? `${base}/onboarding-wizard` : '/onboarding-wizard'} replace />} />
        {/* TQ #6906: Personal-Mode rendert eigene Pages (Pseudo-Org 'personal' hat
            keine Backend-Routes /api/org/personal/...). Org/Staff-Mode: bestehend.
            Marketplace + Aria sind in beiden Modi sinnvoll und nutzen kein
            org-spezifisches Backend-API. */}
        {/* TQ #6933: Dashboard ist jetzt mode-aware (Personal + Org in einer
            Komponente) — PersonalDashboard.tsx entfernt. */}
        <Route index element={<Dashboard organization={org} />} />
        {personalMode && (
          <>
            <Route path="my-resources" element={<Services organization={org} />} />
            <Route path="licenses" element={<Licenses organization={org} />} />
            <Route path="downloads" element={isComingSoon('downloads') ? <ComingSoon feature="Downloads" /> : <Downloads organization={org} />} />
            <Route path="billing" element={<Billing organization={org} />} />
            <Route path="settings" element={<Settings organization={org} />} />
            <Route path="support" element={<Tickets organization={org} />} />
            <Route path="gdata" element={<GDataPartner />} />
            {/* TQ #6962-T4: Personal-Mode "Meine Geräte"-Tab. InfraPage erkennt
                via isPersonalMode() und fetcht /api/me/devices (user-scoped).
                Redirects /infra/devices + /devices analog !personalMode-Block. */}
            <Route path="infra" element={isComingSoon('infra') ? <ComingSoon feature="Infrastruktur" /> : <InfraPage organization={org} />} />
            <Route path="infra/devices" element={<Navigate to={`${base}/infra`} replace />} />
            <Route path="devices" element={<Navigate to={`${base}/infra`} replace />} />
          </>
        )}
        {/* TQ #7977 (ZIELBILD-Unified-Cockpit §3.3 / Phase 8): Bookmark-Redirect-
            Tabelle. with.it-sce.de wird heute vom Unified-Cockpit (org-portal,
            Personal-Mode) bedient. Alte customer-portal Flat-Routes (/dashboard,
            /services, /cortex/usage …) existieren hier nicht mehr und würden auf
            den Catch-All (→ Dashboard) fallen — d.h. ein gespeicherter Bookmark
            verlöre sein Ziel. Diese Navigate-Routen leiten jede Legacy-URL auf die
            korrekte neue Personal-Mode-Page (replace = kein History-Müll). Analog
            zum bestehenden /cortex→/aria-, /devices→/infra-Muster. NUR Personal-
            Mode: im Org-/Staff-Cockpit sind /services, /billing usw. echte Routen. */}
        {personalMode && (
          <>
            <Route path="dashboard" element={<Navigate to={base || '/'} replace />} />
            <Route path="services" element={<Navigate to={`${base}/my-resources`} replace />} />
            <Route path="my-services" element={<Navigate to={`${base}/my-resources`} replace />} />
            <Route path="my-servers" element={<Navigate to={`${base}/my-resources`} replace />} />
            <Route path="my-system" element={<Navigate to={`${base}/my-resources`} replace />} />
            <Route path="my-earnings" element={<Navigate to={`${base}/billing`} replace />} />
            <Route path="billing/earnings" element={<Navigate to={`${base}/billing`} replace />} />
            <Route path="billing/quotations" element={<Navigate to={`${base}/billing`} replace />} />
            <Route path="cortex/chat" element={<Navigate to={`${base}/aria`} replace />} />
            <Route path="cortex/usage" element={<Navigate to={`${base}/aria`} replace />} />
            <Route path="cortex/knowledge" element={<Navigate to={`${base}/aria`} replace />} />
            <Route path="cortex/activity" element={<Navigate to={`${base}/aria`} replace />} />
            <Route path="organization" element={<Navigate to={base || '/'} replace />} />
          </>
        )}
        <Route path="marketplace" element={<Marketplace organization={org} />} />
        {!personalMode && (
          <>
            <Route path="members" element={<Members organization={org} />} />
            <Route path="services" element={<Services organization={org} />} />
            <Route path="licenses" element={<Navigate to="../services#lizenzen" replace />} />
            <Route path="tickets" element={<Tickets organization={org} />} />
            <Route path="auftraege" element={<Tasks organization={org} />} />
            <Route path="billing" element={<Billing organization={org} />} />
            {/* TQ #337 (25.09.2026): Dokumente-Tab buendelt Angebote + Rechnungen +
                Vertraege + Regieberichte + Token-Historie unter eigenen Routen
                (jeder Tab verlinkbar). Alte Direkt-Links leiten dorthin um. */}
            <Route path="dokumente/*" element={<Documents organization={org} />} />
            <Route path="quotations" element={<Navigate to={`${base}/dokumente/angebote`} replace />} />
            <Route path="invoices" element={<Navigate to={`${base}/dokumente/rechnungen`} replace />} />
            {/* TQ #341 (25.09.2026): "Projekte" — rein lesende Sicht auf ERPNext-Projekte
                des Kunden. Endpunkte von platform-workspace (PR #122, main) gebaut,
                Feldnamen am 07.10.2026 bestaetigt (siehe Projects.tsx). */}
            <Route path="projekte" element={<Projects organization={org} />} />
            <Route path="projekte/:name" element={<ProjectDetail organization={org} />} />
          </>
        )}
        {/* TQ #6298: Aria-Page hat jetzt 5 Tabs IN der Page (CortexPage). Routes:
              /aria              -> CortexPage (Tab via ?tab=, Default 'activity')
              /aria/activity     -> /aria (Backwards-Compat zu TQ #6199)
              /aria/settings     -> /aria?tab=persona (Backwards-Compat, Admin landet auf 1. Org-Tab)
              /aria-org-settings -> /aria?tab=persona (Legacy)
              /cortex            -> /aria (Legacy)
            Tabs werden in CortexPage selbst gerendert; /aria/settings als separate Route
            existiert nicht mehr (war TQ #6199 Sidebar-Subitem). */}
        <Route path="aria" element={isComingSoon('aria') ? <ComingSoon feature="Aria" /> : <CortexPage organization={org} />} />
        <Route path="aria/activity" element={<Navigate to={`${base}/aria`} replace />} />
        {/* TQ #8504: Aria-Einstellungen sind aus dem Zahnrad-Flow in den
            "Einstellungen"-Bereich (Tabbar) gewandert. Legacy-Bookmarks landen
            jetzt auf /settings mit dem jeweiligen Tab. */}
        <Route path="aria/settings" element={<Navigate to={`${base}/settings?tab=persona`} replace />} />
        <Route path="cortex" element={<Navigate to={`${base}/aria`} replace />} />
        <Route path="aria-org-settings" element={<Navigate to={`${base}/settings?tab=persona`} replace />} />
        {/* TQ #10589 (SPEC-Cockpit-Verbrauchsanzeige.md §6): neue Route `usage`,
            sibling zu billing/aria — funktioniert in beiden Modi (Usage.tsx
            entscheidet intern via isPersonalMode() zwischen getOrgUsage/getMyUsage). */}
        <Route path="usage" element={<Usage organization={org} />} />
        {!personalMode && (
          <>
            <Route path="downloads" element={isComingSoon('downloads') ? <ComingSoon feature="Downloads" /> : <Downloads organization={org} />} />
            <Route path="infra" element={isComingSoon('infra') ? <ComingSoon feature="Infrastruktur" /> : <InfraPage organization={org} />} />
            {/* TQ #5340: /infra/devices und /devices redirecten zur Tab-Layout-Page.
                `/infra` default-Tab = devices, deshalb kein ?tab=devices noetig. */}
            <Route path="infra/devices" element={<Navigate to={`${base}/infra`} replace />} />
            <Route path="devices" element={<Navigate to={`${base}/infra`} replace />} />
            <Route path="settings" element={<Settings organization={org} />} />
          </>
        )}
        <Route path="*" element={<Navigate to={base || '/'} replace />} />
        </Routes>
      </ErrorBoundary>
    </Layout>
  )
}

// TQ #409: Instanz-Modus kommt aus der Laufzeit-Config (/config.json), nicht
// mehr aus einer Host-Map. mode=shared -> IT-SCE-Portal (Slug-based),
// mode=tenant -> Tenant-Custom-Domain (TQ #5325), mode=personal -> Personal-Modus
// (TQ #6906 Bundle A2: Unified-Cockpit fuer den shared multi-user Tenant
// 'personal'; Pre-Login-Routes werden Ingress-seitig auf customer-portal
// geroutet — das hier ist der Post-Login Layer).

// Pseudo-Organization fuer Personal-Modus. Multi-User-Shared-Tenant 'personal'.
// Kein Backend-Call noetig — alle Pages die org.id/org.slug nutzen bekommen
// 'personal' geliefert, Backend filtert per-User via owner_user_id (Phase B2).
function buildPersonalPseudoOrg(): Organization {
  return {
    id: 0, // bedeutungslos im Personal-Modus
    name: 'Mein Konto',
    slug: 'personal',
    public_token: 'personal',
    member_role: 'owner',
    member_count: 1,
    caller_onboarding_status: 'completed',
  }
}

// Kunden-Instanz (TQ #409): die EINE Org steht in der Config, cockpit-api kennt keine
// Organisation-Ressource. Name = customerLabel, Rolle aus GET /api/org/<orgRef>/whoami
// (Client-Rolle cockpit-owner|admin|operator|viewer -> owner|admin|operator|viewer).
function buildTenantPseudoOrg(role: string): Organization {
  const cfg = getRuntimeConfig()
  return {
    id: 0,
    name: cfg.customerLabel,
    slug: cfg.orgRef ?? '',
    public_token: cfg.orgRef,
    member_role: role.replace(/^cockpit-/, '') || 'viewer',
    member_count: 1,
    caller_onboarding_status: 'completed',
  }
}

// Root route: show org picker, redirect when one is selected.
// Auf Custom-Domain (cockpit.<tenant-domain>) resolved diese Komponente
// die Domain ueber den public by-domain Endpoint und springt direkt in
// das Cockpit dieser einen Org.
function OrgPicker({ onLogout, keycloakApi }: { onLogout: () => void; keycloakApi: any }) {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [customDomainError, setCustomDomainError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      // Custom-Domain-Resolver: keinen Picker zeigen, direkt zur Org.
      if (isCustomDomain()) {
        try {
          const hostname = window.location.hostname
          const resp = await keycloakApi.get(`/api/public/org/by-domain/${encodeURIComponent(hostname)}`)
          const data = resp.data as { slug: string; public_token?: string }
          window.location.replace(`/${data.public_token || data.slug}`)
          return
        } catch (e: any) {
          setCustomDomainError(
            `Diese Domain (${window.location.hostname}) ist noch keiner Organisation zugewiesen. Bitte IT-SCE kontaktieren.`,
          )
          setLoading(false)
          return
        }
      }
      try {
        const response = await keycloakApi.get('/api/organizations/my')
        const orgs: Organization[] = response.data.organizations || []
        setOrganizations(orgs)
        // Auto-redirect if exactly one org — UUID statt Slug
        if (orgs.length === 1) {
          window.location.href = `/${orgs[0].public_token || orgs[0].slug}`
        }
      } catch {
        setOrganizations([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [keycloakApi])

  if (customDomainError) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">IT-SCE</div>
          <p>{customDomainError}</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">IT-SCE</div>
          <div className="spinner"></div>
          <p>Organisationen werden geladen...</p>
        </div>
      </div>
    )
  }

  const handleSelect = (org: Organization) => {
    // UUID-basierte URL fuer Datenschutz (kein lesbarer Org-Slug in der URL)
    window.location.href = `/${org.public_token || org.slug}`
  }

  return (
    <SelectOrganization
      organizations={organizations}
      onSelect={handleSelect}
      onLogout={onLogout}
    />
  )
}

function App() {
  const { authenticated, loading: authLoading, login, logout, api: keycloakApi, token } = useAuth()
  const { loginWithEmail } = useEmailFirstLogin()
  // login_hint auto-login (TQ #6894/#6914): trigger the email-first flow once.
  const loginHintTriggered = useRef(false)

  // TQ #6914: after self-registration (RegisterPage, now native in org-portal),
  // WelcomeStep redirects to with.it-sce.de/?login_hint=<email>. For an
  // UNAUTHENTICATED visitor that URL would otherwise just render the Scout
  // landing page (login_hint ignored), which feels like being thrown out.
  // Instead, auto-run the email-first flow (idp-resolve -> login(undefined,
  // alias)) so the freshly-registered user is taken straight into their broker
  // login. Mirrors customer-portal/src/App.tsx.
  useEffect(() => {
    if (authLoading || authenticated) return
    if (loginHintTriggered.current) return
    const hint = new URLSearchParams(window.location.search).get('login_hint')
    if (hint) {
      loginHintTriggered.current = true
      void loginWithEmail(hint)
    }
  }, [authLoading, authenticated, loginWithEmail])

  // Sync Keycloak-Token an die ApiService-Singleton (services/api.ts).
  // Ohne diesen Hook bleibt token='' → /api/org/:slug/members & friends → 401.
  // Pattern wie customer-portal/src/App.tsx.
  useEffect(() => {
    api.setToken(token ?? '')
  }, [token])

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">IT-SCE</div>
          <div className="spinner"></div>
          <p>Cockpit wird geladen...</p>
        </div>
      </div>
    )
  }

  if (!authenticated) {
    // TQ #6906: Pre-Login auf with.it-sce.de = Scout-Landing (Marketing +
    // public Berater-Chat). Auf cockpit.it-sce.de und cockpit.<tenant>
    // bleibt der einfache Anmelden-Button (B2B kommt direkt mit kc_idp_hint).
    // ErrorBoundary fängt JS-Crash der ScoutLandingPage und fällt auf den
    // Anmelden-Button zurück — so kein weisses Bild fuer Personal-User.
    if (isPersonalMode()) {
      return (
        <ErrorBoundary fallback={
          <div className="loading-screen">
            <div className="loading-content">
              <div className="loading-logo">IT-SCE</div>
              <p>Willkommen bei IT-SCE Workspace</p>
              <button
                onClick={() => login()}
                style={{
                  marginTop: '20px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: 'var(--brand-primary, #FF6B35)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Anmelden
              </button>
            </div>
          </div>
        }>
          <ScoutLandingPage />
        </ErrorBoundary>
      )
    }
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">IT-SCE</div>
          <p>Authentifizierung erforderlich</p>
          <button
            onClick={() => login()}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: 'var(--brand-primary, #FF6B35)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Anmelden
          </button>
          {/* TQ #8773: Personal-Tier-Hint nur auf IT-SCE-eigenen Cockpit-Hosts.
              Privatnutzer (Personal-Users-Realm) haben kein IT-SCE-Org-Konto —
              ihr Portal ist with.it-sce.de. Kein Hint auf Custom-Domain-Cockpits
              (cockpit.eza-wolpertshausen.de etc.). */}
          {!isCustomDomain() && (
            <p style={{ marginTop: '24px', fontSize: '13px', color: '#999', textAlign: 'center' }}>
              Privatnutzer?{' '}
              <a href="https://with.it-sce.de" style={{ color: 'var(--brand-primary, #FF6B35)', textDecoration: 'underline' }}>
                Zum Privatnutzer-Portal wechseln
              </a>
            </p>
          )}
        </div>
      </div>
    )
  }

  // TQ #5387 + #6906: Routing-Split per Hostname.
  //   - Personal-Mode (with.it-sce.de): KEIN Backend-Org-Lookup, Pseudo-Org
  //     'personal' frontend-only. Routes liegen direkt unter /, /aria, ...
  //   - Custom-Domain (cockpit.<tenant-domain>): KEIN URL-Suffix mehr.
  //     Routes liegen direkt unter /, /services, /members usw. Org wird per
  //     by-domain Resolver gefunden.
  //   - Staff-Cockpit (cockpit.it-sce.de): /:slug/* wie bisher + OrgPicker
  //     auf der Root-Route.
  // TQ #6453: /onboarding ist auf beiden Modi explizit gehandhabt damit
  //   ein Re-Onboarding ohne /<slug> moeglich ist (Patric-Live-Test 2026-05-26).
  const onPersonalMode = isPersonalMode()
  const onCustomDomain = isCustomDomain()

  return (
    <ToastProvider>
      {/* TQ #409 (E9): Pfad-Instanz /<kennung>/ -> basename, Legacy ohne basename */}
      <BrowserRouter basename={routerBasename()}>
        <Routes>
          {onPersonalMode ? (
            <Route path="/*" element={<CockpitApp onLogout={logout} keycloakApi={keycloakApi} customDomain personalMode />} />
          ) : onCustomDomain ? (
            <Route path="/*" element={<CockpitApp onLogout={logout} keycloakApi={keycloakApi} customDomain />} />
          ) : (
            <>
              {/* TQ #6453: /onboarding ohne Slug → leitet Admin auf seinen Org-Wizard.
                  Muss VOR /:slug/* matchen, sonst wird "onboarding" als Slug interpretiert. */}
              <Route path="/onboarding" element={<StaffOnboardingRedirect onLogout={logout} keycloakApi={keycloakApi} />} />
              {/* TQ #7128: erste reale /staff/*-Page (Aria-Wissensbasis, read-only).
                  Muss VOR /:slug/* matchen, sonst wird "staff" als Org-Slug interpretiert
                  (= bisheriger tote-Stub-Effekt der Staff-NavLinks). StaffOnly-Guard
                  blendet Nicht-IT-SCE-Member aus. Weitere /staff/*-Pages folgen mit #6919. */}
              <Route path="/staff/knowledge" element={<StaffOnly><StaffKnowledge /></StaffOnly>} />
              {/* TQ #9694 (Staff-Migration Teil A, Bundle #9523): Kunden +
                  Organisationen als Sub-Tabs desselben Hubs, analog zum
                  customer-portal-Vorgänger (StaffCustomersHub). */}
              <Route path="/staff/customers" element={<StaffOnly><StaffCustomersHub /></StaffOnly>} />
              <Route path="/staff/organizations" element={<StaffOnly><StaffCustomersHub /></StaffOnly>} />
              {/* TQ #9695 (Staff-Migration Teil B, Bundle #9523): "Geschäft"
                  Sidebar-Link (Layout.tsx) zeigte bisher ins Leere — Rechnungen,
                  Subscriptions, Subscription-Plans als Tabs unter einer Route. */}
              <Route path="/staff/invoices" element={<StaffOnly><StaffGeschaeft /></StaffOnly>} />
              {/* TQ #9696 (Staff-Migration Teil C, Bundle #9523): Geschäft-Ops als
                  4 Sub-Tabs desselben Hubs (Service-Katalog, Anfragen,
                  Provisioning-Queue, Tenant onboarden). Alle 4 Routen rendern
                  StaffGeschaeftOpsHub, aktiver Tab aus dem Pfad abgeleitet.
                  Muss VOR /:slug/* matchen, sonst wird "staff" als Org-Slug
                  interpretiert. StaffOnly-Guard blendet Nicht-IT-SCE-Member aus. */}
              <Route path="/staff/service-catalog" element={<StaffOnly><StaffGeschaeftOpsHub /></StaffOnly>} />
              <Route path="/staff/service-requests" element={<StaffOnly><StaffGeschaeftOpsHub /></StaffOnly>} />
              <Route path="/staff/provisioning-queue" element={<StaffOnly><StaffGeschaeftOpsHub /></StaffOnly>} />
              <Route path="/staff/tenant-onboarding" element={<StaffOnly><StaffGeschaeftOpsHub /></StaffOnly>} />
              {/* TQ #9697 (Staff-Migration Teil D, Bundle #9523): System-Health +
                  VMs + ERP-Sync als Sub-Tabs desselben Hubs, analog zum
                  customer-portal-Vorgänger. */}
              <Route path="/staff/health" element={<StaffOnly><StaffHealthHub /></StaffOnly>} />
              <Route path="/staff/vms" element={<StaffOnly><StaffHealthHub /></StaffOnly>} />
              <Route path="/staff/sync" element={<StaffOnly><StaffHealthHub /></StaffOnly>} />
              {/* TQ #9698 (Staff-Migration Teil E, Bundle #9523): Cortex-Uebersicht +
                  Standing-Permissions. Eigene Route (statt Sub-Tab in Teil D
                  /staff/health) um Datei-Konflikt mit dem parallel laufenden
                  Teil D (#9697) zu vermeiden — Konsolidierung ist Folge-Task. */}
              <Route path="/staff/cortex" element={<StaffOnly><StaffCortexAdmin /></StaffOnly>} />
              {/* TQ #9699 (Staff-Migration Teil F): Lizenzen + Downloads-Releases,
                  aus customer-portal portiert (2 Tabs in einer Seite, Sidebar hat
                  nur einen "Software-Lizenzen"-Slot). */}
              <Route path="/staff/licenses" element={<StaffOnly><StaffLicenses /></StaffOnly>} />
              {/* TQ #9701 (Staff-Migration Teil G, letzter Sub-Task aus #9523): Verdienst-
                  Konten + Auszahlungen + Registrierungs-Tokens + Aria-Tenants, aus
                  customer-portal portiert (4 Tabs, Sidebar hat nur einen "Verdienst-
                  Konten"-Slot). */}
              <Route path="/staff/earnings" element={<StaffOnly><StaffEarnings /></StaffOnly>} />
              {/* TQ #10589 (SPEC-Cockpit-Verbrauchsanzeige.md §4b/§5): Staff-Uebersicht
                  ueber alle Tenants (Pool-Einheiten + EUR + Klick-Detail mit interner
                  Marge). Muss VOR /:slug/* matchen, sonst wird "staff" als Org-Slug
                  interpretiert. */}
              <Route path="/staff/usage" element={<StaffOnly><StaffUsage /></StaffOnly>} />
              <Route path="/:slug/*" element={<CockpitApp onLogout={logout} keycloakApi={keycloakApi} />} />
              <Route path="/" element={<OrgPicker onLogout={logout} keycloakApi={keycloakApi} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  )
}

// TQ #7128: Route-Guard fuer /staff/*-Pages (Unified-Cockpit, #6919).
//   Nur IT-SCE-Member (useStaff → Mitglied der `itsce`-Org) duerfen rein.
//   Waehrend des Org-Lookups Placeholder rendern (kein Flackern), Nicht-Staff
//   wird auf die Cockpit-Root umgeleitet.
function StaffOnly({ children }: { children: ReactNode }) {
  const { loading, isStaff } = useStaff()
  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Lade…</div>
  }
  if (!isStaff) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

// TQ #6453: Staff-Cockpit /onboarding-Pfad.
//   - User mit genau 1 Org (Owner/Admin) → direkt zum Wizard
//   - User mit mehreren Orgs → OrgPicker mit Hinweis, dass nach Auswahl der
//     Wizard erscheint (geht ueber /<token>/onboarding-wizard)
//   - User ohne Owner/Admin-Rolle in irgendeiner Org → Info-Screen
function StaffOnboardingRedirect({ onLogout, keycloakApi }: { onLogout: () => void; keycloakApi: any }) {
  const [error, setError] = useState<string | null>(null)
  const [pickFrom, setPickFrom] = useState<Organization[] | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        const response = await keycloakApi.get('/api/organizations/my')
        const orgs: Organization[] = response.data.organizations || []
        const adminOrgs = orgs.filter(o => o.member_role === 'owner' || o.member_role === 'admin')

        if (adminOrgs.length === 0) {
          setError('Du bist in keiner Organisation als Owner oder Admin eingetragen. Re-Onboarding ist nur fuer Admins moeglich.')
          return
        }
        if (adminOrgs.length === 1) {
          const target = adminOrgs[0].public_token || adminOrgs[0].slug
          window.location.replace(`/${target}/onboarding-wizard`)
          return
        }
        setPickFrom(adminOrgs)
      } catch (e: any) {
        setError('Organisationen konnten nicht geladen werden.')
      }
    }
    load()
  }, [keycloakApi])

  if (error) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-logo">IT-SCE</div>
          <p>{error}</p>
          <a href="/" style={{ marginTop: '16px', color: 'var(--brand-primary, #FF6B35)' }}>Zurueck</a>
        </div>
      </div>
    )
  }
  if (pickFrom) {
    return (
      <SelectOrganization
        organizations={pickFrom}
        onSelect={(o) => {
          window.location.href = `/${o.public_token || o.slug}/onboarding-wizard`
        }}
        onLogout={onLogout}
      />
    )
  }
  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="loading-logo">IT-SCE</div>
        <div className="spinner"></div>
        <p>Onboarding wird vorbereitet ...</p>
      </div>
    </div>
  )
}

// PUBLIC ROUTE (TQ #6914): self-registration must render WITHOUT a Keycloak
// login gate. The auth gate inside <App> short-circuits to <ScoutLandingPage />
// for any unauthenticated visitor on with.it-sce.de (it returns before the
// authed routes ever mount), so we intercept /registrieren in a separate
// wrapper — before <App> (and thus useAuth / the SSO check) ever runs. A
// prospective user opening with.it-sce.de/registrieren (with or without
// ?token=…) sees the form directly, no login redirect. Kept as its own
// component so <App>'s hook order stays unconditional (rules-of-hooks).
// Mirrors customer-portal/src/App.tsx AppRoot.
function AppRoot() {
  // TQ #8133: Top-Level ErrorBoundary umhuellt den GESAMTEN App-Shell —
  // also den Auth-Gate UND den CockpitApp-Bootstrap (Org-Fetch, Mode-Detection,
  // useEffects). Vorher war nur <Routes> in CockpitApp gewrappt; ein synchroner
  // Throw im Bootstrap (z.B. spezifischer Tenant/Personal-Mode/neuer User)
  // ergab ein WEISSES FENSTER statt eines Fallbacks. Diese aeussere Boundary
  // faengt jeden Render-/Lifecycle-Crash der gesamten App und zeigt die
  // freundliche Fallback-Karte (mit Reload-Button + Fehlermeldung). Asynchrone
  // Fehler/Promise-Rejections, die eine ErrorBoundary NICHT faengt, deckt der
  // globale Handler aus main.tsx (installGlobalErrorHandlers) ab.
  // Die inneren ErrorBoundaries (um <Routes> und um ScoutLandingPage) bleiben
  // fuer feingranulare Fallbacks bestehen.
  // ToastProvider muss App() UMSCHLIESSEN, nicht in dessen eigenem return
  // stehen: App() ruft bereits am eigenen Funktionsanfang useEmailFirstLogin()
  // -> useToast() auf, bevor die Provider-JSX ueberhaupt existiert. Ein
  // Provider im return derselben Komponente ist kein Vorfahre der Hooks, die
  // im Funktionskoerper DAVOR laufen -- das warf bei jedem Render
  // "useToast must be used within a ToastProvider" (weisser Fehlerbildschirm),
  // seit useEmailFirstLogin() in App() eingefuehrt wurde (04.06.2026, TQ
  // #6914), nur nie live aufgefallen, weil org-portal seitdem nicht neu
  // deployed wurde (Fund 24.09.2026, platform-workspace, beim ersten Deploy
  // seit Monaten). Fix: Provider hier aussen anlegen.
  return (
    <ErrorBoundary>
      <ToastProvider>
        {window.location.pathname === '/registrieren' ? <RegisterPage /> : <App />}
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default AppRoot
